import React, { Fragment, useEffect, useRef, useState } from "react";
import PageHeader from "../../common/components/PageHeader";
import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { FiberManualRecord, MovingOutlined } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import theme from "../../theme";
import CommonCard from "../../common/components/CommonCard";
import CounsellingTask from "./CounsellingTask";
import SatAct from "./SatAct";
import UpcomingMilestone from "./UpcomingMilestone";
import CurrentPlan from "../../common/components/CurrentPlan";
import { useDispatch, useSelector } from "react-redux";
import CommonEventIcon from "../../common/components/CommonEventIcon";
import StudentPlan from "./StudentPlan";
import { ExamPlans, ExamTypes } from "../../common/const";
import ApTutoring from "./ApTutoring";
import ProjectMentoring from "./ProjectMentoring";
import { setLoading } from "../../features/authSlice";
import { getAnalytics } from "../../api/studentPerformanceApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { useReactToPrint } from "react-to-print";

const StudentPerformance = () => {
    const dispatch = useDispatch();
    const pdfRef = useRef();
    const { user, loading } = useSelector((state) => state.auth);
    const [insightData, setInsightData] = useState(null);

    const getInitialData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getAnalytics();
            setInsightData(response?.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id) {
            getInitialData();
        }
    }, [user]);

    const handlePrint = useReactToPrint({
        contentRef: pdfRef,
        documentTitle: "Student Performance Report",
    });

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <div ref={pdfRef}>
                <PageHeader
                    title="My Overall Academic Performance Tracker"
                    content={
                        <Fragment>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                                    My DCP Services
                                </Typography>
                                <FiberManualRecord sx={{ fontSize: 8, color: 'text.secondary' }} />
                                <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                                    Student Academic Performance Tracker
                                </Typography>
                            </Stack>
                            <Typography fontSize={12} color={theme.palette.secondary.main} mt={1}>This includes overall performance including SAT/ACT, Project Mentoring, Academic and Skills</Typography>
                        </Fragment>
                    }
                />
                <Grid container spacing={2}>
                    <Grid item md={8} sm={12}>
                        {insightData?.dcp_index_score && (
                            <CommonCard
                                paddingBottom="0px"
                                borderRadius={2}
                                background={'#DCFCE7'}
                                content={
                                    <Box display="flex" gap={1} alignItems="center">
                                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                            <CircularProgress
                                                variant="determinate"
                                                value={100}
                                                size={80}
                                                thickness={6}
                                                sx={{
                                                    color: '#e0e0e0', // Light gray for remaining portion
                                                    position: 'absolute',
                                                    '& .MuiCircularProgress-circle': {
                                                        strokeLinecap: 'round',
                                                    },
                                                }}
                                            />
                                            <CircularProgress
                                                variant="determinate"
                                                value={insightData?.dcp_index_score}
                                                size={80}
                                                thickness={6}
                                                sx={{
                                                    color: insightData?.dcp_index_score
                                                        ? theme.palette.secondary.default// Full green
                                                        : theme.palette.secondary.default, // Your progress color
                                                    '& .MuiCircularProgress-circle': {
                                                        strokeLinecap: 'round',
                                                    },
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    top: 0,
                                                    left: 0,
                                                    bottom: 0,
                                                    right: 0,
                                                    position: 'absolute',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography
                                                    fontSize={17}
                                                    component="div"
                                                    fontWeight={700}
                                                    color="text.primary"
                                                >
                                                    {`${insightData?.dcp_index_score}%`}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box width="90%">
                                            <Typography color="#10AE2D" fontSize={16} fontWeight={600}>DCP Performance Insight</Typography>
                                            <Typography color="background.dark" fontSize={14}>{insightData?.insight_text}</Typography>
                                            <CommonButton
                                                label="Download Report"
                                                marginBottom="0px"
                                                color={theme.palette.background.dark}
                                                background="white"
                                                height="30px"
                                                marginTop={1}
                                                width="150px"
                                                border={`1px solid #0000001A`}
                                                handleClick={handlePrint}
                                            />
                                        </Box>
                                    </Box>
                                }
                            />
                        )}
                        <SatAct />
                        <ApTutoring />
                        <CounsellingTask />
                        <ProjectMentoring />
                    </Grid>
                    <Grid item md={4} mt={2} sm={12}>
                        <CurrentPlan noMoreSession />
                        <CommonCard
                            borderRadius={2}
                            content={
                                <Fragment>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                {/* Gray background circle (always shows full circle) */}
                                                <CircularProgress
                                                    variant="determinate"
                                                    value={100}
                                                    size={50}
                                                    thickness={6}
                                                    sx={{
                                                        color: '#e0e0e0', // Light gray for remaining portion
                                                        position: 'absolute',
                                                        '& .MuiCircularProgress-circle': {
                                                            strokeLinecap: 'round',
                                                        },
                                                    }}
                                                />

                                                {/* Green progress circle on top */}
                                                <CircularProgress
                                                    variant="determinate"
                                                    value={user?.indexScore[user?.student_current_grade] || user?.indexScore?.GRDAE_9 || user?.indexScore?.GRADE_10 || user?.indexScore?.GRADE_11 || user?.indexScore?.GRADE_12}
                                                    size={50}
                                                    thickness={6}
                                                    sx={{
                                                        color: (user?.indexScore[user?.student_current_grade] || user?.indexScore?.GRDAE_9 || user?.indexScore?.GRADE_10 || user?.indexScore?.GRADE_11 || user?.indexScore?.GRADE_12) > 90
                                                            ? theme.palette.secondary.default // Full green
                                                            : (user?.indexScore[user?.student_current_grade] || user?.indexScore?.GRDAE_9 || user?.indexScore?.GRADE_10 || user?.indexScore?.GRADE_11 || user?.indexScore?.GRADE_12) < 90 ?
                                                                theme.palette.primary.main
                                                                : (user?.indexScore[user?.student_current_grade] || user?.indexScore?.GRDAE_9 || user?.indexScore?.GRADE_10 || user?.indexScore?.GRADE_11 || user?.indexScore?.GRADE_12) < 60 ?
                                                                    'red' :
                                                                    theme.palette.secondary.default, // Your progress color
                                                        '& .MuiCircularProgress-circle': {
                                                            strokeLinecap: 'round',
                                                        },
                                                    }}
                                                />

                                                {/* Optional: Center text */}
                                                <Box
                                                    sx={{
                                                        top: 0,
                                                        left: 0,
                                                        bottom: 0,
                                                        right: 0,
                                                        position: 'absolute',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Typography
                                                        fontSize={12}
                                                        component="div"
                                                        fontWeight={700}
                                                        color="text.primary"
                                                    >
                                                        {user?.student_current_grade ? user?.indexScore[user?.student_current_grade] : (user?.indexScore?.GRDAE_9 || user?.indexScore?.GRADE_10 || user?.indexScore?.GRADE_11 || user?.indexScore?.GRADE_12)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Typography fontSize={14}>My Dcp</Typography>
                                                <Typography fontSize={14}>Index Score</Typography>
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                background: '#DBEAFE',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                borderRadius: '50%',
                                                width: 45,
                                                height: 45,
                                            }}
                                        >
                                            <MovingOutlined sx={{ color: '#155DFC' }} />
                                        </Box>
                                    </Box>
                                    <Typography mt={1} fontSize={13} color={theme.palette.secondary.main} fontWeight={600}>DCP index score depends on overall performance of the student.</Typography>
                                    <Box display="flex" justifyContent="space-between" mt={1}>
                                        <Box display="flex" gap={1} alignItems="center">
                                            <CommonEventIcon background="#00C950" />
                                            <Typography fontSize={13} color={theme.palette.background.dark}>Good</Typography>
                                        </Box>
                                        <Box display="flex" gap={1} alignItems="center">
                                            <CommonEventIcon background="#FFAB2D" />
                                            <Typography fontSize={13} color={theme.palette.background.dark}>Average</Typography>
                                        </Box>
                                        <Box display="flex" gap={1} alignItems="center">
                                            <CommonEventIcon background="red" />
                                            <Typography fontSize={13} color={theme.palette.background.dark}>Bad</Typography>
                                        </Box>
                                    </Box>
                                </Fragment>
                            }
                            paddingBottom="0px"
                            padding="13px"
                        />
                        <StudentPlan examType={ExamTypes.ACT_SAT} label="SAT/ACT Coaching" />
                        <UpcomingMilestone />
                        <StudentPlan examType={ExamTypes.ACADEMIC} label="AP/Academic Tutoring" />
                        {ExamPlans.slice(0, 1).map((a, index) => (
                            <CommonCard
                                key={index}
                                paddingBottom="0px"
                                borderRadius={1}
                                padding="10px"
                                content={
                                    <Box
                                        sx={{
                                            background: '#E0E0E080',
                                            borderRadius: 1,
                                            padding: 2
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <img
                                                src={`/Images/${a.name}.png`}
                                                alt="subscription-logo"
                                                height="60px"
                                            />
                                            <Box sx={{ width: '100%' }}>
                                                <Box display="flex" gap={1} alignItems="center" justifyContent="space-between">
                                                    <Typography variant="h5">{a.name}</Typography>
                                                    {a.isActive && (
                                                        <Box
                                                            sx={{
                                                                background: theme.palette.background.main,
                                                                textAlign: 'center',
                                                                borderRadius: 0.8,
                                                                width: 60,
                                                                height: 25,
                                                                marginRight: 1
                                                            }}
                                                        >
                                                            <Typography sx={{ color: theme.palette.success.main }}>Active</Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Typography sx={{ fontWeight: 600 }}>${a.price} <span style={{ color: theme.palette.icon.light, fontWeight: 'normal' }}>/ month</span></Typography>
                                            </Box>
                                        </Box>
                                        <Typography mt={1} color={theme.palette.background.dark}>You're on a solid path with notable strengths in several areas. Your profile shows promise.</Typography>
                                    </Box>
                                }
                                footerContent={
                                    <CommonButton
                                        label="Upgrade"
                                        size="md"
                                        marginBottom="0px"
                                        height='35px'
                                        marginTop="0px"
                                    />
                                }
                            />
                        ))}
                        <StudentPlan examType={ExamTypes.COUNSELLING} label="Counseling Service" />
                        {ExamPlans.slice(1, 2).map((a, index) => (
                            <CommonCard
                                key={index}
                                paddingBottom="0px"
                                borderRadius={1}
                                padding="10px"
                                content={
                                    <Box
                                        sx={{
                                            background: '#E0E0E080',
                                            borderRadius: 1,
                                            padding: 2
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <img
                                                src={`/Images/${a.name}.png`}
                                                alt="subscription-logo"
                                                height="60px"
                                            />
                                            <Box sx={{ width: '100%' }}>
                                                <Box display="flex" gap={1} alignItems="center" justifyContent="space-between">
                                                    <Typography variant="h5">{a.name}</Typography>
                                                    {a.isActive && (
                                                        <Box
                                                            sx={{
                                                                background: theme.palette.background.main,
                                                                textAlign: 'center',
                                                                borderRadius: 0.8,
                                                                width: 60,
                                                                height: 25,
                                                                marginRight: 1
                                                            }}
                                                        >
                                                            <Typography sx={{ color: theme.palette.success.main }}>Active</Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Typography sx={{ fontWeight: 600 }}>${a.price} <span style={{ color: theme.palette.icon.light, fontWeight: 'normal' }}>/ month</span></Typography>
                                            </Box>
                                        </Box>
                                        <Typography mt={1} color={theme.palette.background.dark}>You're on a solid path with notable strengths in several areas. Your profile shows promise.</Typography>
                                    </Box>
                                }
                                footerContent={
                                    <CommonButton
                                        label="Upgrade"
                                        size="md"
                                        marginBottom="0px"
                                        height='35px'
                                        marginTop="0px"
                                    />
                                }
                            />
                        ))}
                        <StudentPlan examType={ExamTypes.COUNSELLING} label="Project Mentoring" />
                        {ExamPlans.slice(2, 3).map((a, index) => (
                            <CommonCard
                                key={index}
                                paddingBottom="0px"
                                borderRadius={1}
                                padding="10px"
                                content={
                                    <Box
                                        sx={{
                                            background: '#E0E0E080',
                                            borderRadius: 1,
                                            padding: 2
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <img
                                                src={`/Images/${a.name}.png`}
                                                alt="subscription-logo"
                                                height="60px"
                                            />
                                            <Box sx={{ width: '100%' }}>
                                                <Box display="flex" gap={1} alignItems="center" justifyContent="space-between">
                                                    <Typography variant="h5">{a.name}</Typography>
                                                    {a.isActive && (
                                                        <Box
                                                            sx={{
                                                                background: theme.palette.background.main,
                                                                textAlign: 'center',
                                                                borderRadius: 0.8,
                                                                width: 60,
                                                                height: 25,
                                                                marginRight: 1
                                                            }}
                                                        >
                                                            <Typography sx={{ color: theme.palette.success.main }}>Active</Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Typography sx={{ fontWeight: 600 }}>${a.price} <span style={{ color: theme.palette.icon.light, fontWeight: 'normal' }}>/ month</span></Typography>
                                            </Box>
                                        </Box>
                                        <Typography mt={1} color={theme.palette.background.dark}>You're on a solid path with notable strengths in several areas. Your profile shows promise.</Typography>
                                    </Box>
                                }
                                footerContent={
                                    <CommonButton
                                        label="Upgrade"
                                        size="md"
                                        marginBottom="0px"
                                        height='35px'
                                        marginTop="0px"
                                    />
                                }
                            />
                        ))}
                    </Grid>
                </Grid>
            </div>
        </Fragment>
    )
}

export default StudentPerformance;